import UserAvatar from '@/common/components/UserAvatars/UserAvatar';
import { useWorkspaceDetails } from '@/features/workspace/services/useWorkspaceQueries';
import { useAuthStore } from '@/stores/authStore';
import {
    Button,
    Checkbox,
    Group,
    Loader,
    Menu,
    Modal,
    Select,
    Stack,
    Text,
    TextInput,
    UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconChevronDown } from '@tabler/icons-react';
import { Suspense, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TeamWithDetail } from '@tickitacka/shared';
import { useCreateProject } from '../services/useProjectQueries';

const CreateProjectModal = ({
    opened,
    onClose,
    workspaceId,
}: {
    opened: boolean;
    onClose: () => void;
    workspaceId: string;
}) => {
    const { t } = useTranslation(['common', 'project']);
    const { user } = useAuthStore.getState();
    const navigate = useNavigate();
    const { mutateAsync: createProject } = useCreateProject();

    // 팀 멤버 정보 있는 워크스페이스 데이터 불러와 내가 속한 팀만 필터링
    const {
        data: workspaceDetails,
        // isLoading: isWorkspaceDetailsLoading,
        // refetch: refetchWorkspaceDetails,
    } = useWorkspaceDetails(workspaceId);

    const myTeams: TeamWithDetail[] = useMemo(() => {
        return (
            workspaceDetails?.teams.filter(team =>
                team?.team_member?.some(member => member.user_id === user.id)
            ) || []
        );
    }, [workspaceDetails, user.id]);

    // Form 설정 - myTeams가 로드된 후 초기값 설정
    const form = useForm({
        initialValues: {
            name: '',
            teamId: myTeams.length > 0 ? myTeams[0].id : '',
            participants: [user?.id],
        },
        validate: {
            name: (value: string) =>
                value.trim().length === 0
                    ? t('project:create_modal.form.validation.name.required')
                    : null,
            teamId: (value: string) =>
                !value
                    ? t('project:create_modal.form.validation.team.required')
                    : null,
        },
    });

    // myTeams가 로드되었을 때 teamId가 비어있다면 첫 번째 팀으로 설정
    useEffect(() => {
        if (myTeams.length > 0 && !form.values.teamId) {
            form.setFieldValue('teamId', myTeams[0].id);
        }
    }, [myTeams.length, form.values.teamId, myTeams, form]);

    // 참여자 선택 이벤트 핸들러
    const handleParticipantChange = (selectedParticipantIds: string[]) => {
        form.setFieldValue('participants', selectedParticipantIds);
    };

    const handleCreateProject = async (values: typeof form.values) => {
        const loadingNotificationId = notifications.show({
            title: t('project:create_modal.notification.in_progress'),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        try {
            const project = await createProject({
                name: values.name,
                team_id: values.teamId,
                leader_id: user.id,
                register_id: user.id,
                participants: Array.from(new Set(values.participants)),
            });

            notifications.update({
                id: loadingNotificationId,
                title: t('project:create_modal.notification.success.title'),
                message: t('project:create_modal.notification.success.message'),
                color: 'green',
                loading: false,
                autoClose: 3000,
            });

            await navigate(`/project/${project.id}`);
        } catch (error) {
            console.error('Failed to create project', error);
            notifications.update({
                id: loadingNotificationId,
                title: t('project:create_modal.notification.error.title'),
                message: t('project:create_modal.notification.error.message'),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        } finally {
            onClose();
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <Modal
                opened={opened}
                onClose={onClose}
                centered
                size="lg"
                title={t('project:create_modal.title')}
                radius={24}
                styles={{
                    title: {
                        fontSize: 20,
                        fontWeight: 600,
                    },
                    header: {
                        padding: '24px 24px 24px 32px',
                    },
                    body: {
                        padding: '0 32px 16px 32px',
                    },
                }}
            >
                <form onSubmit={form.onSubmit(handleCreateProject)}>
                    <Stack>
                        {/* 프로젝트 이름 */}
                        <TextInput
                            {...form.getInputProps('name')}
                            label={t('project:create_modal.name.label')}
                            labelProps={{
                                style: {
                                    fontWeight: 600,
                                },
                            }}
                            styles={{
                                input: {
                                    border: 'none',
                                    fontSize: 24,
                                    fontWeight: 700,
                                    padding: 0,
                                },
                            }}
                            placeholder={t(
                                'project:create_modal.name.placeholder'
                            )}
                            withAsterisk
                        />

                        {/* 팀 선택 */}
                        <Select
                            {...form.getInputProps('teamId')}
                            label={t('project:create_modal.team.label')}
                            labelProps={{
                                style: {
                                    fontWeight: 600,
                                },
                            }}
                            radius={8}
                            placeholder={t(
                                'project:create_modal.team.placeholder'
                            )}
                            rightSection={<IconChevronDown size={16} />}
                            withAsterisk
                            data={myTeams.map(team => ({
                                value: team.id,
                                label: team.name,
                            }))}
                        />

                        {/* 참여자 선택 */}
                        <MemberSelector
                            selectedMemberIds={form.values.participants}
                            onChange={handleParticipantChange}
                            myTeams={myTeams}
                            currentUserId={user.id}
                            currentTeamId={form.values.teamId}
                        />

                        {/* 프로젝트 생성 버튼 */}
                        <Button
                            type="submit"
                            radius={8}
                            disabled={!form.isValid()}
                            w="fit-content"
                            style={{
                                alignSelf: 'flex-end',
                            }}
                        >
                            {t('common:action.create')}
                        </Button>
                    </Stack>
                </form>
            </Modal>
        </Suspense>
    );
};

export default CreateProjectModal;

/**
 * 참여자 선택 컴포넌트 - 팀별로 그룹핑된 멤버 목록 메뉴, 개별 멤버 선택 및 전체 선택/해제 기능 제공
 * @param selectedMemberIds 선택된 참여자 ID 배열
 * @param onChange 참여자 선택 이벤트 핸들러
 * @param myTeams 현재 로그인한 사용자가 속한 팀 목록
 * @param currentUserId 현재 사용자 ID
 * @param currentTeamId 현재 선택된 팀 ID
 */
const MemberSelector = ({
    selectedMemberIds,
    onChange,
    myTeams,
    currentUserId,
    currentTeamId,
}: {
    selectedMemberIds: string[];
    onChange: (selectedMemberIds: string[]) => void;
    myTeams: TeamWithDetail[];
    currentUserId: string;
    currentTeamId: string;
}) => {
    const { t } = useTranslation(['common', 'project']);

    // 팀 정렬: 선택된 팀을 제일 앞으로
    const sortedTeams = useMemo(() => {
        const currentTeam = myTeams.find(team => team.id === currentTeamId);
        const otherTeams = myTeams.filter(team => team.id !== currentTeamId);

        return currentTeam ? [currentTeam, ...otherTeams] : myTeams;
    }, [myTeams, currentTeamId]);

    // 선택된 참여자 이름
    const buttonLabel = useMemo(() => {
        if (selectedMemberIds.length === 0)
            return t('project:create_modal.participants.placeholder');

        // "uid: 이름" 맵 생성
        const memberMap = new Map<string, string>();
        myTeams.forEach(team => {
            team.team_member?.forEach(member => {
                memberMap.set(member.user.id, member.user.name);
            });
        });

        const selectedNames = selectedMemberIds
            .map(id => memberMap.get(id))
            .filter(Boolean);

        return selectedNames.join(', ');
    }, [selectedMemberIds, myTeams, t]);

    // 선택된 참여자 ID Set
    const selectedMemberIdSet: Set<string> = useMemo(
        () => new Set(selectedMemberIds),
        [selectedMemberIds]
    );

    // 팀별 전체 선택/해제 핸들러
    const handleTeamSelectAll = useCallback(
        (teamId: string) => {
            const team = myTeams.find(t => t.id === teamId);
            if (!team) return;

            // 현재 사용자를 제외한 팀 멤버들
            const selectableMembers = team.team_member
                .filter(member => member.user.id !== currentUserId)
                .map(member => member.user.id);

            // 현재 선택된 멤버 중 해당 팀에 속한 멤버가 있는지 확인
            const hasSelectedMembersInTeam = selectableMembers.some(id =>
                selectedMemberIdSet.has(id)
            );

            let newSelectedIds: string[];

            if (hasSelectedMembersInTeam) {
                // 해제: 해당 팀의 모든 멤버를 선택 해제
                newSelectedIds = selectedMemberIds.filter(
                    id => !selectableMembers.includes(id)
                );
            } else {
                // 전체 선택: 팀의 모든 멤버를 선택에 추가
                const newIds = selectableMembers.filter(
                    id => !selectedMemberIdSet.has(id)
                );
                newSelectedIds = [...selectedMemberIds, ...newIds];
            }

            onChange(newSelectedIds);
        },
        [
            myTeams,
            onChange,
            currentUserId,
            selectedMemberIdSet,
            selectedMemberIds,
        ]
    );

    // 팀별 멤버 선택 상태 확인 - 전체 선택/해제 판단을 위해
    const isTeamMemberSelected = useCallback(
        (teamId: string) => {
            const team = myTeams.find(t => t.id === teamId);
            if (!team) return false;

            return team.team_member.some(
                member =>
                    member.user.id !== currentUserId &&
                    selectedMemberIdSet.has(member.user.id)
            );
        },
        [myTeams, selectedMemberIdSet, currentUserId]
    );

    // 참여자 선택 이벤트 핸들러
    const handleSelectMember = (memberId: string) => {
        // 생성자 = 프로젝트 책임자이므로 참여자에서 제거 불가
        if (memberId === currentUserId) return;

        const newValue: string[] = selectedMemberIds.includes(memberId)
            ? selectedMemberIds.filter(id => id !== memberId)
            : [...selectedMemberIds, memberId];

        onChange(newValue);
    };

    return (
        <div>
            {/* label */}
            <Text mb={4} fw={600} size="14px">
                {t('project:create_modal.participants.label')}
                <span style={{ color: '#FA5252' }}>{' *'}</span>
            </Text>

            {/* menu */}
            <Menu closeOnItemClick={false}>
                <Menu.Target>
                    <UnstyledButton
                        w="100%"
                        style={{
                            border: '1px solid #CED4DA',
                            borderRadius: 8,
                            padding: '0.6rem .75rem',
                        }}
                    >
                        <Group>
                            <Text
                                size="14px"
                                flex={1}
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {buttonLabel}
                            </Text>
                            <IconChevronDown size={16} color="#868E96" />
                        </Group>
                    </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown
                    w="556px"
                    mah="30vh"
                    p={8}
                    style={{ overflowY: 'auto' }}
                >
                    {sortedTeams.map((team, teamIndex) => (
                        <div key={team.id}>
                            {/* 팀 헤더 */}
                            <Menu.Label>
                                {team.name}
                                {team.id === currentTeamId &&
                                    t('project:create_modal.team.self')}
                            </Menu.Label>

                            {/* 전체 선택/해제 버튼 */}
                            <Menu.Item
                                p={8}
                                onClick={() => handleTeamSelectAll(team.id)}
                            >
                                <Group gap={5}>
                                    <Checkbox
                                        checked={isTeamMemberSelected(team.id)}
                                        onChange={() =>
                                            handleTeamSelectAll(team.id)
                                        }
                                        size="xs"
                                    />
                                    <Text size="14px">
                                        {isTeamMemberSelected(team.id)
                                            ? t('common:action.unselect_all')
                                            : t('common:action.select_all')}
                                    </Text>
                                </Group>
                            </Menu.Item>

                            {/* 팀 멤버 */}
                            {team.team_member.map(member => (
                                <Menu.Item
                                    p={8}
                                    key={member.user.id}
                                    onClick={() =>
                                        handleSelectMember(member.user.id)
                                    }
                                    style={{
                                        cursor:
                                            member.user.id === currentUserId
                                                ? 'not-allowed'
                                                : 'pointer',
                                    }}
                                >
                                    {member.user.id === currentUserId ? (
                                        <Group gap={5}>
                                            <Checkbox
                                                checked={true}
                                                disabled
                                                size="xs"
                                            />
                                            <UserAvatar
                                                imageUrl={
                                                    member.user
                                                        .profile_image_url
                                                }
                                            />
                                            <Text size="14px">
                                                {member.user.name}
                                                {t(
                                                    'project:create_modal.participants.self'
                                                )}
                                            </Text>
                                        </Group>
                                    ) : (
                                        <Group gap={5}>
                                            <Checkbox
                                                checked={selectedMemberIds.includes(
                                                    member.user.id
                                                )}
                                                size="xs"
                                                onChange={() =>
                                                    handleSelectMember(
                                                        member.user.id
                                                    )
                                                }
                                            />
                                            <UserAvatar
                                                imageUrl={
                                                    member.user
                                                        .profile_image_url
                                                }
                                            />
                                            <Text size="14px">
                                                {member.user.name}
                                            </Text>
                                        </Group>
                                    )}
                                </Menu.Item>
                            ))}

                            {/* 팀 간 구분선 (마지막 팀 제외) */}
                            {teamIndex < sortedTeams.length - 1 && (
                                <Menu.Divider my={4} />
                            )}
                        </div>
                    ))}
                </Menu.Dropdown>
            </Menu>
        </div>
    );
};
